<script setup>
import { computed, useSlots } from 'vue';
import {
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogOverlay,
    AlertDialogPortal,
    AlertDialogRoot,
    AlertDialogTitle,
    VisuallyHidden,
} from 'reka-ui';
import { alertDialogVariants } from './index.ts';

const props = defineProps({
    /**
     * Heading text shown inside the auto-rendered header.
     * If omitted (and no `#header` slot is provided) the header is not rendered.
     * A `VisuallyHidden` fallback is rendered to keep `aria-labelledby` valid.
     */
    title: { type: String, default: undefined },
    /**
     * Subtitle text rendered below the title. Wired to `aria-describedby` for free.
     * A `VisuallyHidden` fallback is rendered when omitted.
     */
    description: { type: String, default: undefined },
    /**
     * Width preset for the panel.
     * @values 'sm', 'md', 'lg', 'xl'
     * @default 'sm'
     */
    size: {
        type: String,
        default: 'sm',
        validator: (v) => ['sm', 'md', 'lg', 'xl'].includes(v),
    },
    /**
     * Extra classes merged onto the panel. Use to override z-index / width at call sites.
     */
    contentClass: { type: [String, Array, Object], default: undefined },
    /**
     * When `true`, an empty `AlertDialogTitle`/`AlertDialogDescription` is rendered inside a
     * `VisuallyHidden` block whenever `title`/`description` aren't passed. Set `false` when the
     * default slot already renders its own `AlertDialogTitle`/`AlertDialogDescription` to avoid
     * duplicate labelled-by targets.
     * @default true
     */
    accessibleHeading: { type: Boolean, default: true },
});

defineEmits(['after:enter', 'after:leave']);

const isOpen = defineModel('open', { type: Boolean, default: undefined });

const slots = useSlots();

const ui = computed(() => alertDialogVariants({ size: props.size }));
</script>

<template>
    <AlertDialogRoot v-model:open="isOpen">
        <AlertDialogPortal>
            <AlertDialogOverlay :class="ui.overlay()" />
            <AlertDialogContent
                :class="ui.content({ class: contentClass })"
                @after-enter="$emit('after:enter')"
                @after-leave="$emit('after:leave')"
            >
                <VisuallyHidden v-if="accessibleHeading && (!title || !description)">
                    <AlertDialogTitle v-if="!title" />
                    <AlertDialogDescription v-if="!description" />
                </VisuallyHidden>

                <header
                    v-if="title || description || slots.header"
                    :class="ui.header()"
                >
                    <AlertDialogTitle
                        v-if="title"
                        :class="ui.title()"
                    >
                        {{ title }}
                    </AlertDialogTitle>
                    <AlertDialogDescription
                        v-if="description"
                        :class="ui.description()"
                    >
                        {{ description }}
                    </AlertDialogDescription>
                    <slot name="header" />
                </header>

                <div :class="ui.body()">
                    <slot />
                </div>

                <footer
                    v-if="slots.action || slots.cancel"
                    :class="ui.footer()"
                >
                    <AlertDialogCancel
                        v-if="slots.cancel"
                        as-child
                    >
                        <slot name="cancel" />
                    </AlertDialogCancel>
                    <AlertDialogAction
                        v-if="slots.action"
                        as-child
                    >
                        <slot name="action" />
                    </AlertDialogAction>
                </footer>
            </AlertDialogContent>
        </AlertDialogPortal>
    </AlertDialogRoot>
</template>
